import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { PURGE } from 'redux-persist'

import type { AdminStateType, TabType, EditMenuType } from '@redux/types'
import { firstAdminTap } from '@redux/store/core/config.store'
import config from '@configs'
import { I18nType } from '@schemas'

const adminMenu = config.adminMenu()
export const defaultCreateTempMenu: EditMenuType = {
	id: 'newFolder',
	parent: 'root',
	name: 'New Folder',
	type: 'web',
	path: 'root/newFolder',
	uri: '/new-folder',
	icon: 'cog',
	order: 0,
	isActivate: false,
	i18n: [],
}

export const emptyMenu: EditMenuType = {
	id: '',
	parent: '',
	name: '',
	type: 'web',
	path: '',
	uri: '',
	icon: '',
	order: 0,
	isActivate: false,
	i18n: [],
}

const initialState: AdminStateType = {
	// trigger
	triggerGoToAdmin: false,
	triggerLeaveToAdmin: false,
	triggerAdminMenuPage: false,
	triggerCreateMenuPage: false,
	triggerGetMenuList: false,
	triggerCreateMenu: false,
	triggerModifyMenu: false,
	triggerDeleteMenu: false,
	triggerCreateMenuSuccessResult: false,
	triggerModifyMenuSuccessResult: false,
	triggerDeleteMenuSuccessResult: false,

	// menu
	menu: adminMenu,
	menuTreeList: [],
	editTempMenu: { ...defaultCreateTempMenu },
	editMenuMode: 'update',
	selectedMenu: { ...emptyMenu },
	unduplicatedName: '',
	menuOpenModal: '',

	// menu action
	updateMultipleMenuList: [],
	updateMenuI18n: {
		id: '',
		tableName: '',
		tableId: '',
		menuId: '',
		isoLanguageCode: '',
		title: '',
		value: '',
	},

	// nav
	selectedTabIdx: '1',
	tabList: [firstAdminTap],
}

const slice = createSlice({
	name: 'admin',
	initialState,
	reducers: {
		// trigger
		TRIGGER_GO_TO_ADMIN: (state: AdminStateType, action: PayloadAction<boolean>) => {
			state.triggerGoToAdmin = action.payload
		},
		TRIGGER_LEAVE_TO_ADMIN: (state: AdminStateType, action: PayloadAction<boolean>) => {
			state.triggerLeaveToAdmin = action.payload
		},
		TRIGGER_ADMIN_MENU_PAGE: (state: AdminStateType, action: PayloadAction<boolean>) => {
			state.triggerAdminMenuPage = action.payload
		},
		TRIGGER_CREATE_MENU_PAGE: (state: AdminStateType, action: PayloadAction<boolean>) => {
			state.triggerCreateMenuPage = action.payload
		},
		TRIGGER_GET_MENU_LIST: (state: AdminStateType, action: PayloadAction<boolean>) => {
			state.triggerGetMenuList = action.payload
		},
		TRIGGER_CREATE_MENU: (state: AdminStateType, action: PayloadAction<boolean>) => {
			state.triggerCreateMenu = action.payload
		},
		TRIGGER_MODIFY_MENU: (state: AdminStateType, action: PayloadAction<boolean>) => {
			state.triggerModifyMenu = action.payload
		},
		TRIGGER_DELETE_MENU: (state: AdminStateType, action: PayloadAction<boolean>) => {
			state.triggerDeleteMenu = action.payload
		},
		TRIGGER_CREATE_MENU_SUCCESS_RESULT: (
			state: AdminStateType,
			action: PayloadAction<boolean>,
		) => {
			state.triggerCreateMenuSuccessResult = action.payload
		},
		TRIGGER_MODIFY_MENU_SUCCESS_RESULT: (
			state: AdminStateType,
			action: PayloadAction<boolean>,
		) => {
			state.triggerModifyMenuSuccessResult = action.payload
		},
		TRIGGER_DELETE_MENU_SUCCESS_RESULT: (
			state: AdminStateType,
			action: PayloadAction<boolean>,
		) => {
			state.triggerDeleteMenuSuccessResult = action.payload
		},

		// menu
		MENU_TREE_LIST: (
			state: AdminStateType,
			action: PayloadAction<AdminStateType['menuTreeList']>,
		) => {
			state.menuTreeList = action.payload
		},
		EDIT_TEMP_MENU: (state: AdminStateType, action: PayloadAction<EditMenuType>) => {
			state.editTempMenu = action.payload
		},
		EDIT_MENU_MODE: (
			state: AdminStateType,
			action: PayloadAction<AdminStateType['editMenuMode']>,
		) => {
			state.editMenuMode = action.payload
		},
		SELECTED_MENU: (
			state: AdminStateType,
			action: PayloadAction<AdminStateType['selectedMenu']>,
		) => {
			state.selectedMenu = action.payload
		},
		UNDUPLICATED_NAME: (state: AdminStateType, action: PayloadAction<string>) => {
			state.unduplicatedName = action.payload
		},
		MENU_OPEN_MODAL: (state: AdminStateType, action: PayloadAction<string>) => {
			state.menuOpenModal = action.payload
		},

		// menu action
		UPDATE_MULTIPLE_MENU_LIST: (
			state: AdminStateType,
			action: PayloadAction<AdminStateType['menuTreeList']>,
		) => {
			state.updateMultipleMenuList = action.payload
		},
		UPDATE_MENU_I18N: (state: AdminStateType, action: PayloadAction<I18nType>) => {
			state.updateMenuI18n = action.payload
		},

		// nav
		SELECTED_TAB_IDX: (state: AdminStateType, action: PayloadAction<string>) => {
			state.selectedTabIdx = action.payload
		},
		TAB_LIST: (state: AdminStateType, action: PayloadAction<TabType[]>) => {
			state.tabList = action.payload
		},
	},

	extraReducers: builder => {
		builder.addCase(PURGE, () => initialState)
	},
})

export const adminActions = slice.actions
export default slice.reducer
